import config from "../conf/config.json";
import moment from "moment";
import firebase from "../lib/firebase";

// MediaContent (https://mediacontent.apiborne.com) is the external signage
// content editor. A display url points at ecran_accueil.html, and the same
// query string re-opens the config form, as documented in its accueil_form.html:
//   editUrl = storedUrl.replace('ecran_accueil.html', 'accueil_form.html');
const displayPage = 'ecran_accueil.html';
const formPage = 'accueil_form.html';

// Production host, accepted even when config.mediaContentBaseUrl points at a
// local dev server: sequences saved in production keep their edit button.
const prodHost = 'mediacontent.apiborne.com';

export const isMediaContentUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  try {
    const parsed = new URL(url);
    const base = new URL(config.mediaContentBaseUrl);
    // Compare hosts rather than a startsWith on the raw string, so that
    // http/https and a trailing slash don't change the result.
    return parsed.host === base.host || parsed.host === prodHost;
  }
  catch (error) {
    // Not an absolute url (relative path, plain text, google doc id...)
    return false;
  }
};

// Same refresh rule as ApolloClient's authLink: regenerate the Firebase id
// token when the stored one is more than 30 minutes old.
const getAuthTokenForCallback = async () => {
  let token = localStorage.getItem('authToken');
  const genTimeToken = localStorage.getItem('genTimeToken');
  if (moment().unix() - parseInt(genTimeToken) > 60 * 30 && firebase.currentUser) {
    token = await firebase.currentUser.getIdToken(true);
    localStorage.setItem('authToken', token);
    localStorage.setItem('genTimeToken', moment().unix());
  }
  return token;
};

// Appends the params that let the MediaContent editor save back to castReactor
// ("Sauvegarder dans castReactor" button in accueil_form.html).
// - cr_origin: postMessage target, always set (new content flow + live refresh).
// - cr_seq / cr_second: identify the sequence zone being edited.
// - cr_api / cr_brand / cr_ms / cr_token: let the editor call the graphQlApi
//   updateSequenceUrl mutation directly, so the save works even if this tab
//   is closed. Only set for the edit flow (opts.seqId + brandId +
//   mediaSequenceId provided).
// Async because the auth token may need a refresh: call window.open('')
// synchronously in the click handler and set win.location with the result,
// otherwise popup blockers kick in.
export const withMediaContentCallbackParams = async (url, opts = {}) => {
  const parsed = new URL(url);
  parsed.searchParams.set('cr_origin', window.location.origin);
  if (opts.seqId) {
    parsed.searchParams.set('cr_seq', opts.seqId);
  }
  if (opts.second) {
    parsed.searchParams.set('cr_second', '1');
  }
  if (opts.seqId && opts.brandId && opts.mediaSequenceId) {
    const token = await getAuthTokenForCallback();
    if (token) {
      parsed.searchParams.set('cr_api', config.graphQlUrl);
      parsed.searchParams.set('cr_brand', opts.brandId);
      parsed.searchParams.set('cr_ms', opts.mediaSequenceId);
      parsed.searchParams.set('cr_token', token);
    }
  }
  return parsed.toString();
};

// True when a window "message" event is a save callback coming from the
// MediaContent editor (origin restricted to the configured editor or prod).
export const isMediaContentSaveMessage = (event) => {
  try {
    const base = new URL(config.mediaContentBaseUrl);
    if (event.origin !== base.origin && event.origin !== 'https://' + prodHost) {
      return false;
    }
  }
  catch (error) {
    return false;
  }
  return Boolean(event.data
    && event.data.source === 'mediacontent-editor'
    && event.data.type === 'save'
    && event.data.url);
};

// Url of the MediaContent editor form with no parameters: opens the form
// blank, to build a brand-new content from scratch.
export const getMediaContentNewUrl = () => {
  const base = config.mediaContentBaseUrl.endsWith('/')
    ? config.mediaContentBaseUrl
    : config.mediaContentBaseUrl + '/';
  return base + formPage;
};

// Returns the editor url for a MediaContent display url, or null when the url
// is not one. Keeps the query string untouched: the form reads ?config=,
// ?config_url= and ?site= itself to prefill its fields.
export const getMediaContentEditUrl = (url) => {
  if (!isMediaContentUrl(url)) {
    return null;
  }

  const parsed = new URL(url);
  if (parsed.pathname.endsWith(formPage)) {
    // Already an editor url, nothing to rewrite.
    return parsed.toString();
  }

  if (parsed.pathname.endsWith(displayPage)) {
    parsed.pathname = parsed.pathname.replace(displayPage, formPage);
    return parsed.toString();
  }

  // Root or index.html url: point at the form, preserving any parameters.
  parsed.pathname = parsed.pathname.replace(/[^/]*$/, formPage);
  return parsed.toString();
};
